import React, { useEffect, useMemo, useRef, useState } from "react";
import HelpLink from "../help/HelpLink";
import { cn } from "../../lib/utils";
import {
  EFFORT_COVERAGE,
  describeFitness,
  describeIntensity,
  describeLoadTrend,
  describeMinutes,
  formLabel,
  ratedShare,
} from "../../utils/trainingLoad";

const CHART_HEIGHT = 72; // px, bars only

// Same colours as the target rows on the Last 7 days card.
const KINDS = [
  ["strength", "Strength", "bg-red-400"],
  ["hard", "Intervals", "bg-amber-400"],
  ["cardio", "Cardio", "bg-sky-400"],
  ["other", "Other", "bg-foreground/40"],
];

// Load by heart-rate band (#203), bottom to top. Effort-only load (lifting,
// sessions without a strap) can't be split, so it's grey.
const BANDS = [
  ["effort", "Effort only", "bg-foreground/40"],
  ["easy", "Easy", "bg-blue-500"],
  ["threshold", "Threshold", "bg-amber-500"],
  ["vo2max", "VO2max", "bg-red-500"],
];

const DAY_MS = 24 * 60 * 60 * 1000;

/** "49% easy · 45% threshold · 6% VO2max", from the heart-rate part of a week's load. */
const splitLine = (w) => {
  const i = w.intensity || {};
  const hr = (i.easy || 0) + (i.threshold || 0) + (i.vo2max || 0);
  if (!(hr > 0)) return null;
  const pct = (v) => Math.round((v / hr) * 100);
  return `${pct(i.easy)}% easy · ${pct(i.threshold)}% threshold · ${pct(i.vo2max)}% VO2max`;
};

/** Two or three round gridlines that cover the tallest week. */
const gridlines = (max, unit) => {
  const steps = unit === "min" ? [30, 60, 100, 200, 300, 500] : [100, 250, 500, 1000, 2000, 5000];
  const step = steps.find((s) => max / s <= 3) || steps[steps.length - 1];
  const top = Math.max(step, Math.ceil(max / step) * step);
  const lines = [];
  for (let v = step; v <= top; v += step) lines.push(v);
  return { top, lines };
};

/**
 * Training by week, last 8 weeks (#94). Minutes is the default: every session
 * has a length, so it's a fair picture of how much and what kind. Load (effort
 * x minutes, or heart-rate zones where recorded, #202) only appears once most
 * sessions have one; below that it charts which weeks you remembered to rate,
 * not how hard you trained. For
 * information only: nothing here warns or changes the plan.
 *
 * The Load view splits each bar by heart-rate band and draws fitness (#203)
 * over it as a typical week, so both share the bars' scale; fatigue and form
 * are in words. `fitness` is fitnessSeries() or null.
 *
 * This week (still going) is drawn lighter. Hover or tap a bar for its
 * figures: the other weeks fade and a mark sits under it. A hidden table
 * carries the same numbers.
 */
const WeeklyTrainingCard = ({ weeks, fitness = null }) => {
  const [active, setActive] = useState(null);
  const [view, setView] = useState("minutes");
  // Touch has no hover, so a tapped week stays until you tap it again or tap
  // anywhere off the bars. Hover (a mouse) previews a week as before.
  const barsRef = useRef(null);
  useEffect(() => {
    if (active == null) return undefined;
    const away = (e) => {
      if (!barsRef.current?.contains(e.target)) setActive(null);
    };
    document.addEventListener("pointerdown", away);
    return () => document.removeEventListener("pointerdown", away);
  }, [active]);
  const share = ratedShare(weeks);
  const effortReady = share >= EFFORT_COVERAGE;
  const showing = effortReady ? view : "minutes";

  const value = (w) => (showing === "minutes" ? w.minutes : w.load);
  // Fitness as a typical week, for the days the chart covers.
  const fitnessLine = useMemo(() => {
    if (showing !== "load" || !fitness?.days?.length || !weeks?.length) return null;
    const start = weeks[0].start.getTime();
    const span = weeks.length * 7;
    const points = fitness.days
      .filter((d) => d.date.getTime() >= start)
      .map((d) => ({ x: (Math.round((d.date.getTime() - start) / DAY_MS) + 0.5) / span, v: d.fitness * 7, date: d.date }));
    return points.length > 1 ? points : null;
  }, [fitness, weeks, showing]);
  const max = useMemo(
    () =>
      Math.max(
        1,
        ...(weeks || []).map((w) => (showing === "minutes" ? w.minutes : w.load)),
        ...(fitnessLine || []).map((p) => p.v),
      ),
    [weeks, showing, fitnessLine],
  );
  if (!weeks?.length || !weeks.some((w) => w.minutes > 0)) return null;

  const { top, lines } = gridlines(max, showing === "minutes" ? "min" : "load");
  const shown = active != null ? weeks[active] : null;
  const kindsUsed = KINDS.filter(([k]) => weeks.some((w) => w.byKind?.[k] > 0));
  const bandsUsed = BANDS.filter(([k]) => weeks.some((w) => w.intensity?.[k] > 0));
  const split = showing === "load" && bandsUsed.some(([k]) => k !== "effort");
  // Fitness at the end of a tapped week (or today, for this week).
  const fitnessAt = (w) => {
    if (!fitnessLine) return null;
    const end = w.start.getTime() + 7 * DAY_MS;
    const before = fitnessLine.filter((p) => p.date.getTime() < end);
    return before.length ? Math.round(before[before.length - 1].v) : null;
  };
  const totalSessions = weeks.reduce((sum, w) => sum + w.sessions, 0);
  const totalRated = weeks.reduce((sum, w) => sum + w.rated, 0);

  const readout = shown
    ? `${shown.current ? "This week so far" : shown.range}: ${
        showing === "minutes"
          ? `${shown.minutes} min`
          : [`${shown.load.toLocaleString("en-AU")} load`, splitLine(shown)].filter(Boolean).join(" · ")
      } · ${shown.sessions} ${shown.sessions === 1 ? "session" : "sessions"}`
    : (showing === "minutes" ? describeMinutes(weeks) : describeLoadTrend(weeks)) || " ";
  const shownFitness = shown ? fitnessAt(shown) : null;
  const readout2 =
    showing !== "load" || !fitnessLine
      ? null
      : shown
        ? shownFitness != null && `Fitness that week: ${shownFitness.toLocaleString("en-AU")} a week`
        : describeFitness(fitness);
  const intensityLine = split ? describeIntensity(weeks) : null;

  return (
    <section
      aria-label="Training by week"
      className="grid gap-3 rounded-2xl border border-border bg-card p-3.5"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-1 text-[15px] font-bold text-white">
          {showing === "minutes" ? "Minutes by week" : "Training load"}
          <HelpLink topic="progress#training-by-week" label="About training by week" />
        </h3>
        {effortReady && (
          <div
            role="radiogroup"
            aria-label="Chart"
            className="inline-flex overflow-hidden rounded-lg border border-border text-[11px]"
          >
            {[
              ["minutes", "Minutes"],
              ["load", "Load"],
            ].map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={view === id}
                onClick={() => {
                  setView(id);
                  setActive(null);
                }}
                className={cn(
                  "min-h-[28px] px-2.5",
                  view === id ? "bg-white/10 text-foreground" : "text-muted-foreground",
                )}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid gap-1.5">
        {/* Readout for the bar under the pointer, in text colours. */}
        <p
          className="min-h-[1.25rem] text-xs tabular-nums text-muted-foreground"
          aria-live="polite"
        >
          {readout}
          {readout2 && (
            <>
              <br />
              {/* Today's form, grey whatever the state: information, not a warning.
                  It sits here, not by the title, which has no room at phone width. */}
              {!shown && (
                <>
                  Form{" "}
                  <span className="inline-block rounded-full border border-border px-1.5 text-[10px] font-semibold leading-4 text-foreground/80">
                    {formLabel(fitness.formPct)}
                  </span>
                  {" · "}
                </>
              )}
              {readout2}
            </>
          )}
        </p>
        <div className="grid grid-cols-[1.75rem_minmax(0,1fr)] gap-1.5" aria-hidden="true">
          {/* Value scale, so a bar's height means something. */}
          <div className="relative text-[10px] tabular-nums text-muted-foreground" style={{ height: CHART_HEIGHT }}>
            {lines.map((v) => (
              <span
                key={v}
                className="absolute right-0 -translate-y-1/2 leading-none"
                style={{ top: CHART_HEIGHT - (v / top) * CHART_HEIGHT }}
              >
                {v >= 1000 ? `${v / 1000}k` : v}
              </span>
            ))}
          </div>
          <div className="relative">
            {lines.map((v) => (
              <span
                key={v}
                className="absolute inset-x-0 border-t border-white/[0.06]"
                style={{ top: CHART_HEIGHT - (v / top) * CHART_HEIGHT }}
              />
            ))}
            <div
              className="relative grid items-end gap-[2px] border-b border-border/60"
              style={{
                gridTemplateColumns: `repeat(${weeks.length}, minmax(0, 1fr))`,
                height: CHART_HEIGHT,
              }}
              ref={barsRef}
              onPointerLeave={(e) => e.pointerType === "mouse" && setActive(null)}
            >
              {weeks.map((w, i) => {
                const v = value(w);
                const height = v > 0 ? Math.max(3, Math.round((v / top) * CHART_HEIGHT)) : 0;
                const load = w.load || 1;
                return (
                  <button
                    key={w.start.getTime()}
                    type="button"
                    tabIndex={-1}
                    onPointerEnter={(e) => e.pointerType === "mouse" && setActive(i)}
                    onClick={() => setActive(active === i ? null : i)}
                    className="relative flex h-full items-end justify-center"
                  >
                    <span
                      className={cn(
                        "flex w-full max-w-[28px] flex-col-reverse overflow-hidden rounded-t-[4px]",
                        active != null && active !== i ? "opacity-30" : w.current && "opacity-60",
                      )}
                      style={{ height }}
                    >
                      {showing === "minutes" && w.minutes > 0 ? (
                        KINDS.map(([k, , colour]) =>
                          w.byKind?.[k] > 0 ? (
                            <span
                              key={k}
                              className={cn("block w-full", colour)}
                              style={{ height: `${(w.byKind[k] / w.minutes) * 100}%` }}
                            />
                          ) : null,
                        )
                      ) : split && w.load > 0 ? (
                        BANDS.map(([k, , colour]) =>
                          w.intensity?.[k] > 0 ? (
                            <span
                              key={k}
                              className={cn("block w-full", colour)}
                              style={{ height: `${(w.intensity[k] / load) * 100}%` }}
                            />
                          ) : null,
                        )
                      ) : (
                        <span className="block h-full w-full bg-foreground/70" />
                      )}
                    </span>
                  </button>
                );
              })}
              {fitnessLine && (
                <>
                  <svg
                    className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
                    viewBox={`0 0 1000 ${CHART_HEIGHT}`}
                    preserveAspectRatio="none"
                  >
                    {[
                      ["hsl(var(--card))", 4],
                      ["hsl(var(--foreground))", 2],
                    ].map(([stroke, width]) => (
                      <path
                        key={width}
                        d={fitnessLine
                          .map((p, j) => `${j ? "L" : "M"}${(p.x * 1000).toFixed(1)},${(CHART_HEIGHT - (p.v / top) * CHART_HEIGHT).toFixed(1)}`)
                          .join("")}
                        fill="none"
                        stroke={stroke}
                        strokeWidth={width}
                        strokeLinejoin="round"
                        vectorEffect="non-scaling-stroke"
                      />
                    ))}
                  </svg>
                  <span
                    className="pointer-events-none absolute h-[7px] w-[7px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-card bg-foreground"
                    style={{
                      left: `${fitnessLine[fitnessLine.length - 1].x * 100}%`,
                      top: CHART_HEIGHT - (fitnessLine[fitnessLine.length - 1].v / top) * CHART_HEIGHT,
                    }}
                  />
                </>
              )}
            </div>
            <div
              className="mt-1 grid text-[10px] tabular-nums text-muted-foreground"
              style={{ gridTemplateColumns: `repeat(${weeks.length}, minmax(0, 1fr))` }}
            >
              {weeks.map((w, i) => (
                <span key={w.start.getTime()} className="flex flex-col items-center gap-0.5 text-center">
                  {/* Marks the tapped week, in place of an outline that clashed with the stack. */}
                  <span className={cn("h-[3px] w-3 rounded-full", active === i ? "bg-foreground" : "bg-transparent")} />
                  {i === weeks.length - 1 && w.current
                    ? "Now"
                    : i % 2 === (weeks.length - 1) % 2
                      ? `${w.start.getDate()}/${w.start.getMonth() + 1}`
                      : ""}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {showing === "minutes" && kindsUsed.length > 1 && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground" aria-hidden="true">
          {kindsUsed.map(([k, label, colour]) => (
            <span key={k} className="inline-flex items-center gap-1.5">
              <span className={cn("h-2 w-2 rounded-[2px]", colour)} />
              {label}
            </span>
          ))}
        </div>
      )}
      {split && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground" aria-hidden="true">
          {bandsUsed.map(([k, label, colour]) => (
            <span key={k} className="inline-flex items-center gap-1.5">
              <span className={cn("h-2 w-2 rounded-[2px]", colour)} />
              {label}
            </span>
          ))}
          {fitnessLine && (
            <span className="inline-flex items-center gap-1.5">
              <span className="h-[2px] w-3 rounded-full bg-foreground" />
              Fitness
            </span>
          )}
        </div>
      )}
      {intensityLine && <p className="text-xs text-muted-foreground">{intensityLine}</p>}
      {!effortReady && totalRated > 0 && (
        <p className="text-xs text-muted-foreground">
          Training load shows once most sessions have an effort rating or heart rate (
          {totalRated} of {totalSessions} so far).
        </p>
      )}

      {/* A table ignores width: 1px and stretches to fit its text, so the
          wrapper is what hides it without widening the page. */}
      <div className="sr-only">
        <table>
          <caption>Training by week</caption>
          <thead>
            <tr>
              <th scope="col">Week</th>
              <th scope="col">Minutes</th>
              {KINDS.map(([k, label]) => (
                <th key={k} scope="col">
                  {label} minutes
                </th>
              ))}
              <th scope="col">Load</th>
              {BANDS.map(([k, label]) => (
                <th key={k} scope="col">
                  {label} load
                </th>
              ))}
              <th scope="col">Sessions</th>
            </tr>
          </thead>
          <tbody>
            {weeks.map((w) => (
              <tr key={w.start.getTime()}>
                <th scope="row">{w.current ? `${w.range} (this week so far)` : w.range}</th>
                <td>{w.minutes}</td>
                {KINDS.map(([k]) => (
                  <td key={k}>{w.byKind?.[k] || 0}</td>
                ))}
                <td>{w.rated ? w.load : "no rating or heart rate"}</td>
                {BANDS.map(([k]) => (
                  <td key={k}>{w.intensity?.[k] || 0}</td>
                ))}
                <td>{w.sessions}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default WeeklyTrainingCard;
